import { useState, useEffect, useRef, useCallback } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../components/LoadingSpinner";
import SocialNetworks from "./socialNetworks/SocialNetworks";
import { handleCreateTicket, sendEmail } from "../utility/createDownloadTicket";
import { getOrganization, updateParticipant } from "../utility";
import ENV from "../env";
import { getPublicCheckoutStatus } from "../utility/publicCheckout";
import default_org from "./logos/logoPaddedBlackBg.png";

const Checkout = (props) => {
  const { t, i18n } = useTranslation(["checkout", "eventdesc"]);
  const eventId = props.event.id;
  const [checkoutState, setCheckoutState] = useState(null);
  const [verificationError, setVerificationError] = useState(false);
  const [retryVerification, setRetryVerification] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [ticketFile, setTicketFile] = useState(null);
  const [isPreparingTickets, setIsPreparingTickets] = useState(false);
  const [ticketUrl, setTicketUrl] = useState(null);
  const preparationVersion = useRef(0);
  const [organization, setOrganization] = useState(null);
  const confirmedParticipant = useRef(null);
  const pdfPromise = useRef(null);
  const automaticPreparationStarted = useRef(false);
  const pdfEmailAttempt = useRef(null);
  const [pdfEmailError, setPdfEmailError] = useState(false);
  const participantId = props.participant?.id;
  const sessionId = new URLSearchParams(window.location.search).get("session_id") || undefined;
  const alert = useAlert();
  const source = props.participant;
  const policyParticipant = checkoutState?.ticketParticipant || source;
  const automaticEmailDisabled = checkoutState?.automaticTicketEmailEnabled === false
    || (checkoutState?.automaticTicketEmailEnabled === undefined
      && eventId === "1770713130627"
      && (policyParticipant?.promoCodeId || source?.promoCodeId)
      && !source?.partnerOrderId && !source?.stripeSessionId
      && !(Number(source?.paymentReceivedCents || 0) > 0)
      && Array.isArray(policyParticipant?.tickets) && policyParticipant.tickets.length > 0
      && policyParticipant.tickets.every(line => {
        const amount = line.totalPrice ?? line.sum;
        return amount !== null && amount !== undefined && amount !== ""
          && Number.isFinite(Number(amount)) && Number(amount) === 0;
      }));

  const eventDescription = (() => {
    const descriptions = props.event?.descriptions;
    if (descriptions && typeof descriptions === "object" && Object.keys(descriptions).length) {
      return descriptions[i18n.language] || descriptions[Object.keys(descriptions)[0]] || "";
    }
    return props.event?.description || "";
  })();

  useEffect(() => {
    if (!eventId || !participantId) return undefined;
    let active = true, timer;
    setIsLoading(true);
    setVerificationError(false);
    setPdfEmailError(false);
    setCheckoutState(null);
    setTicketFile(null);
    confirmedParticipant.current = null;
    pdfPromise.current = null;
    automaticPreparationStarted.current = false;
    preparationVersion.current += 1;
    setIsPreparingTickets(false);
    const poll = async () => {
      try {
        const state = await getPublicCheckoutStatus({ eventId, participantId, sessionId });
        if (!active) return;
        setCheckoutState(state);
        setVerificationError(false);
        if (state.canDownload && state.ticketParticipant) confirmedParticipant.current = state.ticketParticipant;
        else if (state.paymentStatus === "pending" || (state.paymentStatus === "paid" && state.fulfillmentStatus === "pending")) timer = setTimeout(poll, 10000);
      } catch (error) {
        if (active) setVerificationError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [eventId, participantId, sessionId, retryVerification]);

  useEffect(() => {
    if (props.event?.orgId) {
      getOrganization(props.event.orgId)
        .then((orgData) => setOrganization(orgData))
        .catch((error) => console.error("Failed to load organization:", error));
    }
  }, [props.event?.orgId]);

  const prepareTickets = useCallback(async () => {
    if (!checkoutState?.canDownload || !confirmedParticipant.current) return null;
    if (pdfPromise.current) return pdfPromise.current;
    const participant = confirmedParticipant.current;
    const version = ++preparationVersion.current;
    setIsPreparingTickets(true);
    pdfPromise.current = handleCreateTicket({ participant, event: props.event, lang: i18n.language })
      .then(ticket => {
        if (!ticket?.size) throw new Error("The ticket PDF is empty");
        if (preparationVersion.current !== version) return null;
        setTicketFile(ticket);
        return ticket;
      })
      .catch(() => {
        if (preparationVersion.current === version) {
          pdfPromise.current = null;
          alert.show(t("ticket_download_failed"), { type: "error" });
        }
        return null;
      })
      .finally(() => {
        if (preparationVersion.current === version) setIsPreparingTickets(false);
      });
    return pdfPromise.current;
  }, [checkoutState?.canDownload, props.event, i18n.language, alert, t]);
  const sendTicketPdfIfNeeded = useCallback(async (file) => {
    const participant = confirmedParticipant.current;
    if (!file || !checkoutState?.canDownload || !participant || participant.mailSent
      || automaticEmailDisabled) return;
    if (pdfEmailAttempt.current?.key !== `${eventId}/${participant.id}`) {
      pdfEmailAttempt.current = { key: `${eventId}/${participant.id}`, sent: false, marked: false, promise: null };
    }
    const attempt = pdfEmailAttempt.current;
    if (attempt.marked) return;
    if (attempt.promise) return attempt.promise;
    attempt.promise = (async () => {
      try {
        if (!attempt.sent) {
          await sendEmail({
            ticket: file, participant, event: props.event, lang: i18n.language,
            message: props.event?.ticketEmailMessage,
            successUrl: `${ENV.hostname}/checkout/${eventId}/${participant.id}/success`,
          });
          attempt.sent = true;
        }
        // Persist only after the PDF email succeeds. A persistence retry must not resend it.
        await updateParticipant({ id: participant.id, eventId, markMailSent: true });
        attempt.marked = true;
        participant.mailSent = true;
        if (confirmedParticipant.current === participant) {
          setPdfEmailError(false);
          setCheckoutState((state) => ({ ...state, mailSent: true, deliveryStatus: "sent" }));
        }
      } catch (error) {
        if (confirmedParticipant.current === participant) {
          setPdfEmailError(true);
          alert.show(t("ticket_email_failed"), { type: "error" });
        }
      } finally {
        attempt.promise = null;
      }
    })();
    return attempt.promise;
  }, [checkoutState?.canDownload, automaticEmailDisabled, props.event, i18n.language, eventId, alert, t]);

  useEffect(() => {
    if (checkoutState?.canDownload && confirmedParticipant.current && !automaticPreparationStarted.current) {
      automaticPreparationStarted.current = true;
      const preparation = prepareTickets();
      const version = preparationVersion.current;
      preparation.then((file) => {
        if (version === preparationVersion.current) sendTicketPdfIfNeeded(file);
      });
    }
  }, [checkoutState?.canDownload, prepareTickets, sendTicketPdfIfNeeded]);

  useEffect(() => {
    if (!ticketFile) {
      setTicketUrl(null);
      return undefined;
    }
    // A real, already prepared link preserves the mobile browser's user gesture.
    const url = URL.createObjectURL(ticketFile.type === "application/pdf"
      ? ticketFile : new Blob([ticketFile], { type: "application/pdf" }));
    setTicketUrl({ file: ticketFile, url });
    return () => {
      // Only release after leaving/replacing this PDF, allowing an opened viewer
      // to finish loading. Never expire a link while it is still on screen.
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
  }, [ticketFile]);

  useEffect(() => () => { preparationVersion.current += 1; }, []);

  const readyTicketUrl = ticketUrl?.file === ticketFile ? ticketUrl?.url : null;
  const retryTicketPreparation = async () => {
    const file = await prepareTickets();
    if (file) sendTicketPdfIfNeeded(file);
  };

  const goToSale = () => {
    window.open(`${ENV.hostname}/events/${eventId}`, "_self", "noreferrer");
  };

  const showOnMap = () => {
    const eventLocation = props.event?.eventLocation;
    if (!eventLocation) return;
    window.open(`https://maps.google.com?q=${eventLocation.latitude},${eventLocation.longitude}`, "_blank");
  };

  if (isLoading) {
    return (
      <section className="event-desc event-desc-section">
        <div className="container">
          <div className="section-header">
            <h2 className="title">{t("loading")}</h2>
            <LoadingSpinner height="30%" width="30%" />
          </div>
        </div>
      </section>
    );
  }

  if (verificationError || !checkoutState?.canDownload) {
    const title = verificationError ? "public_verification_failed"
      : checkoutState?.fulfillmentStatus === "resolved_manual" ? "public_manually_resolved"
      : checkoutState?.fulfillmentStatus === "blocked" ? "public_payment_blocked"
      : ["failed", "expired"].includes(checkoutState?.paymentStatus) ? "transaction_failed"
      : checkoutState?.paymentStatus === "paid" ? "public_preparing_tickets" : "public_payment_pending";
    return (
      <section className="event-desc event-desc-section">
        <div className="container">
          <div className="__details-form checkout-success-message" role="status" aria-live="polite">
            <h2 className="title">{t(title)}</h2>
            <p className="text">{t(title === "public_payment_blocked" || title === "public_manually_resolved" ? "public_contact_organizer" : title === "public_verification_failed" ? "public_verification_help" : "public_confirmation_help")}</p>
            <p className="text">{t("public_order_reference")}: <code>{participantId}</code></p>
            <div className="__ticket-checkout-buttons">
              <button type="button" className="checkoutBtn" onClick={() => setRetryVerification(value => value + 1)}>{t("retry")}</button>
              <button type="button" className="checkoutBackBtn" onClick={goToSale}>{t("back_to_sale")}</button>
            </div>
            {organization?.org?.contactEmail && <a className="contactBtn" href={`mailto:${organization.org.contactEmail}`}>{t("contact", { ns: "eventdesc" })}</a>}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="event-desc event-desc-section">
      <div className="container">
        <div className="__details-wrapper checkout-success-layout">
          <div className="__details-wrapper-right checkout-success-right">
            <div className="__details-form checkout-success-message">
              <h3 className="__details-form-title">{t("registration_successful")}</h3>
              <p className="text">{t(automaticEmailDisabled ? "registration_message_download_only" : "registration_message")}</p>
              {pdfEmailError && (
                <div role="status">
                  <p>{t("ticket_email_failed")}</p>
                  <button type="button" className="checkoutBtn" onClick={async () => sendTicketPdfIfNeeded(ticketFile || await prepareTickets())}>
                    {t("retry")}
                  </button>
                </div>
              )}
            </div>

            <div className="__ticket-checkout">
              <div className="__ticket-checkout-titles">
                <div className="subtitle">{t("download_tickets_now")}</div>
              </div>
              <div className="__ticket-checkout-buttons">
                {readyTicketUrl ? (
                  <a className="checkoutBtn" href={readyTicketUrl} download="tickets.pdf">
                    {t("download_tickets")}
                  </a>
                ) : (
                  <button type="button" className="checkoutBtn" onClick={retryTicketPreparation}
                    disabled={isPreparingTickets || !!ticketFile} aria-busy={isPreparingTickets || !!ticketFile}>
                    {t(isPreparingTickets || ticketFile ? "preparing_pdf" : "download_tickets")}
                  </button>
                )}
                <button type="button" className="checkoutBackBtn" onClick={goToSale}>
                  {t("back_to_sale")}
                </button>
              </div>
              {readyTicketUrl && (
                <p className="text checkout-pdf-help">
                  {t("open_pdf_help")} {" "}
                  <a href={readyTicketUrl} target="_blank" rel="noopener noreferrer">{t("open_pdf")}</a>
                </p>
              )}
            </div>
          </div>

          <div className="__details-wrapper-left">
            {props.event?.name && (
              <div className="event-sale-title-block">
                <h1 className="event-sale-title">{props.event.name}</h1>
              </div>
            )}

            <div className="__details-wrapper-left-description">
              <h3 className="__details-wrapper-left-description-title">
                {t("description", { ns: "eventdesc" })}
              </h3>
              {eventDescription ? <p className="event-sale-description">{eventDescription}</p> : null}
            </div>

            {props.event?.eventAddress && props.event?.eventLocation && (
              <div className="__localization-form">
                <h3 className="__localization-form-title">{t("localization", { ns: "eventdesc" })}</h3>
                <div className="__ticket-location">
                  <div className="__ticket-location-cont">
                    <div className="__ticket-location-innercont">
                      <span className="info">{t("your_address", { ns: "eventdesc" })}</span>
                      <span className="subtitle">{props.event.eventAddress}</span>
                    </div>
                    <div className="right-detail">
                      <div className="apply-promo-container">
                        <button onClick={showOnMap} className="applyPromo">
                          {t("show_on_map", { ns: "eventdesc" })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ticket-map">
                  <iframe
                    title={t("localization", { ns: "eventdesc" })}
                    width="100%"
                    height="100%"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                      props.event.eventLocation.longitude - 0.01
                    },${props.event.eventLocation.latitude - 0.01},${
                      props.event.eventLocation.longitude + 0.01
                    },${props.event.eventLocation.latitude + 0.01}&layer=mapnik&marker=${
                      props.event.eventLocation.latitude
                    },${props.event.eventLocation.longitude}`}
                  />
                </div>
              </div>
            )}

            {props.event?.eventFromDate && props.event?.eventToDate && (
              <div className="__ticket-calendar">
                <h3 className="__ticket-calendar-title">{t("calendar", { ns: "eventdesc" })}</h3>
                <div className="__ticket-calendar-items">
                  <div className="__ticket-date">
                    <div className="__ticket-date-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M28.334 4.99935H35.0007C35.4427 4.99935 35.8666 5.17494 36.1792 5.4875C36.4917 5.80006 36.6673 6.22399 36.6673 6.66602V33.3327C36.6673 33.7747 36.4917 34.1986 36.1792 34.5112C35.8666 34.8238 35.4427 34.9993 35.0007 34.9993H5.00065C4.55862 34.9993 4.1347 34.8238 3.82214 34.5112C3.50958 34.1986 3.33398 33.7747 3.33398 33.3327V6.66602C3.33398 6.22399 3.50958 5.80006 3.82214 5.4875C4.1347 5.17494 4.55862 4.99935 5.00065 4.99935H11.6673V1.66602H15.0007V4.99935H25.0007V1.66602H28.334V4.99935ZM6.66732 14.9993V31.666H33.334V14.9993H6.66732ZM10.0007 21.666H18.334V28.3327H10.0007V21.666Z"
                          fill="#8BA4BF"
                        />
                      </svg>
                    </div>
                    <div className="__ticket-date-cont">
                      <span className="info">{t("start", { ns: "eventdesc" })}</span>
                      <span className="subtitle">
                        {new Date(props.event.eventFromDate).toLocaleString(i18n.language, {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="__ticket-date">
                    <div className="__ticket-date-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M28.334 4.99935H35.0007C35.4427 4.99935 35.8666 5.17494 36.1792 5.4875C36.4917 5.80006 36.6673 6.22399 36.6673 6.66602V33.3327C36.6673 33.7747 36.4917 34.1986 36.1792 34.5112C35.8666 34.8238 35.4427 34.9993 35.0007 34.9993H5.00065C4.55862 34.9993 4.1347 34.8238 3.82214 34.5112C3.50958 34.1986 3.33398 33.7747 3.33398 33.3327V6.66602C3.33398 6.22399 3.50958 5.80006 3.82214 5.4875C4.1347 5.17494 4.55862 4.99935 5.00065 4.99935H11.6673V1.66602H15.0007V4.99935H25.0007V1.66602H28.334V4.99935ZM6.66732 14.9993V31.666H33.334V14.9993H6.66732ZM10.0007 21.666H18.334V28.3327H10.0007V21.666Z"
                          fill="#8BA4BF"
                        />
                      </svg>
                    </div>
                    <div className="__ticket-date-cont">
                      <span className="info">{t("end", { ns: "eventdesc" })}</span>
                      <span className="subtitle">
                        {new Date(props.event.eventToDate).toLocaleString(i18n.language, {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {organization !== null && (
              <div className="__ticket-contact-details">
                <h3 className="__ticket-calendar-title">{t("contact", { ns: "eventdesc" })}</h3>
                <div className="__ticket-contact-area">
                  <div className="__ticket-contact-area-container">
                    <div className="__ticket-contact-area-image">
                      <img
                        src={organization.org.logo || props?.event?.logoUrl || default_org}
                        alt={t("logo", { ns: "eventdesc" })}
                      />
                    </div>
                    <div className="__ticket-contact-area-title">
                      <div className="title">{organization.org.organization}</div>
                      <div className="email">{organization.org?.contactEmail}</div>
                    </div>
                  </div>
                  <div className="__ticket-contact-area-button">
                    {organization.org?.contactEmail ? (
                      <a
                        className="contactBtn"
                        style={{ textDecoration: "none" }}
                        href={`mailto:${organization.org.contactEmail}`}
                      >
                        {t("contact", { ns: "eventdesc" })}
                      </a>
                    ) : (
                      <button className="contactBtn" disabled>
                        {t("no_contact_available", { ns: "eventdesc" })}
                      </button>
                    )}
                  </div>
                </div>
                <SocialNetworks eventId={eventId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
